import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import AuthShell, { AuthDivider } from '../components/AuthShell';

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [googleEnabled, setGoogleEnabled] = useState(false);
  const googleBtnRef = useRef();

  useEffect(() => {
    api.get('/auth/config')
      .then(r => setGoogleEnabled(r.data.google_enabled !== false))
      .catch(() => setGoogleEnabled(false));
  }, []);

  useEffect(() => {
    if (!googleEnabled || !window.google || !process.env.REACT_APP_GOOGLE_CLIENT_ID || !googleBtnRef.current) return;
    window.google.accounts.id.initialize({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      callback: handleGoogleCredential,
    });
    window.google.accounts.id.renderButton(googleBtnRef.current, {
      theme: 'outline',
      size: 'large',
      width: googleBtnRef.current?.offsetWidth || 360,
      text: 'signin_with',
    });
  }, [googleEnabled]);

  async function handleGoogleCredential({ credential }) {
    setLoading(true);
    try {
      const r = await api.post('/auth/google', { credential });
      login(r.data.token, r.data.user);
      nav('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Google sign-in failed');
    }
    setLoading(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post('/auth/login', form);
      login(r.data.token, r.data.user);
      nav('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Login failed');
    }
    setLoading(false);
  }

  return (
    <AuthShell
      eyebrow="Welcome back"
      title="Sign in to TuWay"
      subtitle="Pick up every conversation right where you left it."
    >
        {googleEnabled && process.env.REACT_APP_GOOGLE_CLIENT_ID && (
          <>
            <div ref={googleBtnRef} className="auth-google" />
            <AuthDivider>or continue with email</AuthDivider>
          </>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group auth-field">
            <label className="label">Email address</label>
            <input className="input" type="email" required autoFocus
              value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              placeholder="you@example.com" />
          </div>
          <div className="form-group auth-field">
            <label className="label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Password</span>
              <Link to="/forgot-password" className="auth-inline-link">Forgot password?</Link>
            </label>
            <input className="input" type="password" required
              value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              placeholder="••••••••" />
          </div>
          <button type="submit" className={'btn btn-primary btn-lg btn-block' + (loading ? ' is-loading' : '')} disabled={loading}>
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
        <p className="auth-switch">
          New to TuWay? <Link to="/register">Create an account</Link>
        </p>
    </AuthShell>
  );
}
